import React, { useState, useEffect, useMemo } from 'react';
import { Container, Button, Typography, Box, CircularProgress, Divider } from '@mui/material';
import axios from 'axios';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import 'dayjs/locale/pt-br';
import 'dayjs/locale/en';
import { useSnackbar } from "../providers/SnackBarProvider";
import MainDailyCashList from '../layouts/MainDailyCashList';
import {
  formatCurrency,
  handleError,
  formatDateForMySQL,
  resolveDateFormattingLocale,
  bcp47ToDayjsLocale,
  getDayjsDateFormatForLocale,
} from '../utils/lib';
import { useConfig } from '../providers/ConfigProvider';
import { useAppBar } from '../providers/AppBarProvider';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const MainDailyCash = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [cashData, setCashData] = useState([]);
  const [totalIn, setTotalIn] = useState(0);
  const [totalOut, setTotalOut] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const snackbar = useSnackbar();
  const {config} = useConfig();
  const { updateTitle, toggleFab } = useAppBar();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const dateFormattingLocale = useMemo(() => resolveDateFormattingLocale(config), [config]);
  const datePickerFormat = useMemo(
    () => getDayjsDateFormatForLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );
  const dayjsPickerLocale = useMemo(
    () => bcp47ToDayjsLocale(dateFormattingLocale),
    [dateFormattingLocale]
  );

  useEffect(() => {
    // Reset to first page when date changes
    toggleFab(true);
    
    if (config.rol === 'USER') {
      updateTitle('', t('main.personalDailyCash'));
    } else {
      updateTitle('', t('main.dailyCash'));
    }
    
    setCurrentPage(1);
    setCashData([]);
    fetchDailyCashData(1);
  }, [selectedDate]);

  const fetchDailyCashData = async (page) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const formattedDate = formatDateForMySQL(selectedDate);
      const params = {
        daily_cash: '',
        page: page,
        date: formattedDate
      };
      
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}finance`, 
        { params, withCredentials: true }
      );
      
      if (response.data && response.data.data) {
        const data = response.data.data;
        
        if (page === 1) {
          setCashData(data.content || []);
        } else {
          setCashData(prev => [...prev, ...(data.content || [])]);
        }
        
        setTotalIn(data.total_in || 0);
        setTotalOut(data.total_out || 0);
        setHasMorePages(page < data.total_pages);
        setCurrentPage(page);
      }
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleLoadMore = () => {
    if (hasMorePages && !isLoadingMore) {
      fetchDailyCashData(currentPage + 1);
    }
  };

  const handleItemClick = (item) => {
    if (item.deleted === 1) {
      snackbar(t('dailyCash.recordDeleted'));
      return;
    }

    switch (item.type) {
      case "SELL":
        navigate('/main/view-sell', { state: { id: item.id } });
        break;
      case "MANUAL_CASH_IN":
      case "MANUAL_CASH_OUT":
        navigate('/main/view-cash-movement', { state: { id: item.id } });
        break;
      case "PURCHASE":
        navigate('/main/view-purchase', { state: { id: item.id } });
        break;
      case "SPENDING":
        navigate('/main/view-spending', { state: { id: item.id } });
        break;
      default:
        break;
    }
  };

  const handleViewDetails = () => {
    const formattedDate = formatDateForMySQL(selectedDate);
    navigate(`/main/view-daily-cash-details/${formattedDate}`);
  };

  const balance = totalIn - totalOut;

  if (loading) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );

  return (
    <Container maxWidth="xl">
      <Box 
        sx={{ 
          width: '100%', 
          borderRadius: 2,
          boxShadow: 3,
          bgcolor: 'background.paper',
          mb: 2,
          p: 2
        }}
      >

        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <DatePicker
            label={t('dailyCash.date')}
            value={selectedDate}
            onChange={handleDateChange}
            slotProps={{ 
              textField: { 
                fullWidth: true,
              } 
            }}
            format={datePickerFormat}
          />
        </LocalizationProvider>
        
        {cashData.length > 0 ? (
          <>
            <MainDailyCashList
              cashItems={cashData}
              onClick={handleItemClick}
            />
            
            {hasMorePages && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('common.loadMore')
                  )}
                </Button>
              </Box>
            )}
            
            <Divider sx={{ my: 1 }} />

            <Box sx={{ p: 1 }}>
              <Typography variant="body1" gutterBottom>
                {t('dailyCash.income')} {formatCurrency(totalIn)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {t('dailyCash.expenses')} {formatCurrency(totalOut)}
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ 
                fontWeight: 'bold', 
                color: balance >= 0 ? 'success.main' : 'error.main' 
              }}>
                {t('dailyCash.balance')} {formatCurrency(balance)}
              </Typography>
              <Button 
                variant="text" 
                color="primary" 
                onClick={handleViewDetails}
                sx={{ py: 0.5 }}
              >
                {t('common.viewDetail')}
              </Button>
            </Box>
          </>
        ) : (
          <Typography
            variant="subtitle1"
            sx={{ textAlign: 'center', py: 4 }}
          >
            {t('dailyCash.noMovements')}
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default MainDailyCash;
